// Design-only screens (D-044): features seen in the Zalient admin (2026-10-21) that Adyvance does
// not have yet, drawn in our own layout so the owner can review them before the features are built.
// Each is listed in docs/ZALIENT_DESIGN_GAPS.md with its canonical ID. Nothing here reads or writes data.
import type { ComponentType } from "react";
import { t } from "@ady/i18n";
import type { NavItem } from "../shell/nav";
import { DesignPage, EmptyTable, Pills } from "./kit";
import { STUDIO_SCREENS } from "./studio";

type Screen = ComponentType<{ item: NavItem }>;

const Newsletter: Screen = ({ item }) => (
  <DesignPage
    item={item}
    lead={t("designs.newsletter.lead")}
    action={t("designs.newsletter.action")}
    actionIcon="upload"
  >
    <EmptyTable
      caption={t(item.label)}
      icon="mail"
      columns={[
        t("designs.newsletter.cols.email"),
        t("designs.newsletter.cols.signedUp"),
        t("designs.newsletter.cols.source"),
        t("designs.newsletter.cols.consent"),
      ]}
      title={t("designs.newsletter.emptyTitle")}
      body={t("designs.newsletter.emptyBody")}
    />
  </DesignPage>
);

const FlashDeals: Screen = ({ item }) => (
  <DesignPage
    item={item}
    lead={t("designs.flashDeals.lead")}
    action={t("designs.flashDeals.action")}
  >
    <Pills
      labels={[
        t("designs.flashDeals.filters.running"),
        t("designs.flashDeals.filters.scheduled"),
        t("designs.flashDeals.filters.ended"),
      ]}
    />
    <EmptyTable
      caption={t(item.label)}
      icon="timer"
      columns={[
        t("designs.flashDeals.cols.deal"),
        t("designs.flashDeals.cols.products"),
        t("designs.flashDeals.cols.discount"),
        t("designs.flashDeals.cols.starts"),
        t("designs.flashDeals.cols.ends"),
        t("designs.status"),
      ]}
      title={t("designs.flashDeals.emptyTitle")}
      body={t("designs.flashDeals.emptyBody")}
    />
  </DesignPage>
);

const Links: Screen = ({ item }) => (
  <DesignPage item={item} lead={t("designs.links.lead")} action={t("designs.links.action")}>
    <EmptyTable
      caption={t(item.label)}
      icon="link"
      columns={[
        t("designs.links.cols.link"),
        t("designs.links.cols.items"),
        t("designs.links.cols.discount"),
        t("designs.links.cols.opens"),
        t("designs.links.cols.orders"),
        t("designs.status"),
      ]}
      title={t("designs.links.emptyTitle")}
      body={t("designs.links.emptyBody")}
    />
  </DesignPage>
);

const Affiliates: Screen = ({ item }) => (
  <DesignPage
    item={item}
    lead={t("designs.affiliates.lead")}
    action={t("designs.affiliates.action")}
  >
    <div className="grid gap-3 sm:grid-cols-3">
      {(["orders", "sales", "owed"] as const).map((k) => (
        <div key={k} className="flex flex-col gap-1 rounded-xs border border-card bg-surface p-4">
          <span className="text-xs text-fg-secondary">{t(`designs.affiliates.stats.${k}`)}</span>
          <span className="ady-ledger text-title-sm font-bold text-fg-tertiary">
            {t("designs.notLive")}
          </span>
        </div>
      ))}
    </div>
    <Pills
      labels={[t("designs.affiliates.filters.active"), t("designs.affiliates.filters.paused")]}
    />
    <EmptyTable
      caption={t(item.label)}
      icon="share"
      columns={[
        t("designs.affiliates.cols.partner"),
        t("designs.affiliates.cols.code"),
        t("designs.affiliates.cols.products"),
        t("designs.affiliates.cols.commission"),
        t("designs.affiliates.cols.orders"),
        t("designs.affiliates.cols.earned"),
      ]}
      title={t("designs.affiliates.emptyTitle")}
      body={t("designs.affiliates.emptyBody")}
    />
  </DesignPage>
);

/** Nav item id → its design screen. Placeholder renders these instead of "Coming soon". */
export const DESIGN_SCREENS: Record<string, Screen> = {
  ...STUDIO_SCREENS,
  newsletter: Newsletter,
  flashDeals: FlashDeals,
  links: Links,
  affiliates: Affiliates,
};
